import { convertWeight, convertBloodGlucose, isCountedDelivery, isCountOnlyPillShot } from './utils';
import { TITRATION_PRESETS } from './constants';
import { calcAdherence } from './adherence';
import { buildSymptomTiming, describeInsight, TIMING_MIN_ELIGIBLE } from './symptomTiming';
import { isBlend, shotBreakdown, formatBreakdown, ingredientTotals, vialForShot as resolveShotVial, splitShotConc } from './blend';
import { matchAll, shotRecallInfo } from './recalls';

// The report is assembled as an HTML string, so EVERY user-controlled value (stack item labels,
// titration text, sites, symptom names) must go through esc() — an unescaped "<" or "&" (easy to
// get via Stack Import) would otherwise break or inject into the document.
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ESC[c]);

export const rangeLabel = days => (days === 365 ? 'Last 12 Months' : `Last ${days} Days`);

const num1 = n => String(parseFloat(Number(n).toFixed(1)));
const num2 = n => String(parseFloat(Number(n).toFixed(2)));

// Small inline-SVG line chart (no JS, prints cleanly). `series` = [{ pts: [{t, v}], color }]; the first
// series decides whether there is enough data (2+ points). Flat data draws a mid-line, never divides by 0.
function lineChart({ key, title, series, caption }) {
  if (!series[0] || series[0].pts.length < 2) return '';
  const W = 300, H = 70, PAD = 6;
  const all = series.flatMap(s => s.pts);
  const tMin = Math.min(...all.map(p => p.t)), tMax = Math.max(...all.map(p => p.t));
  const vMin = Math.min(...all.map(p => p.v)), vMax = Math.max(...all.map(p => p.v));
  const x = t => (tMax === tMin ? W / 2 : PAD + ((t - tMin) / (tMax - tMin)) * (W - 2 * PAD));
  const y = v => (vMax === vMin ? H / 2 : PAD + (1 - (v - vMin) / (vMax - vMin)) * (H - 2 * PAD));
  const lines = series.map(s =>
    `<polyline points="${s.pts.map(p => `${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')}" fill="none" stroke="${s.color}" stroke-width="1.6" stroke-linejoin="round"/>`
  ).join('');
  return `<div class="chart"><div class="sec-lbl">${title}</div>
<svg data-chart="${key}" viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none"><rect x="0" y="0" width="${W}" height="${H}" fill="#f4f8fc" rx="6"/>${lines}</svg>
<div class="cap">${caption}</div></div>`;
}

export function buildHealthSummaryHtml({ allShots, checkIns, stored, getCmpd, cmpdKeys, currentStreak, pillStreak, streakEnabled, weightUnit, bloodGlucoseUnit, rangeDays = 90, now = new Date(), recalls = [] }) {
  const rangeStart = new Date(now.getTime() - rangeDays * 86400000);
  const label = rangeLabel(rangeDays);
  const wu = weightUnit || 'kg';
  const bgu = bloodGlucoseUnit || 'mg/dL';
  const hasInjectableCmpd = cmpdKeys.some(k => getCmpd(k)?.deliveryType !== 'pill');
  const hasPillCmpd = cmpdKeys.some(k => getCmpd(k)?.deliveryType === 'pill');
  const streakStat = !streakEnabled
    ? '&mdash;'
    : hasInjectableCmpd && hasPillCmpd
      ? `${currentStreak}&#128137; / ${pillStreak}&#128138;`
      : hasPillCmpd ? pillStreak : currentStreak;

  const fmt = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtS = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const recentShots = allShots.filter(s => new Date(s.date) >= rangeStart);
  const recentCIs = checkIns.filter(c => new Date(c.date) >= rangeStart);

  // Callers (and the tests) may pass `stored` without `compounds`; getCmpd is always available.
  const recallMatches = matchAll({
    vials: stored.vials,
    compounds: { ...Object.fromEntries(cmpdKeys.map(k => [k, getCmpd(k)])), ...(stored.compounds || {}) },
  }, recalls);
  const recallText = info => info.source === 'fda'
    ? `Recalled lot ${info.vial.lot || '(lot not recorded)'} — ${info.recall.firm}${info.recall.reason ? `: ${info.recall.reason}` : ''}${info.match === 'possible' ? ' (possible match)' : ''}`
    : `Marked recalled by user ${info.vial.recall.date}${info.vial.recall.reason ? `: ${info.vial.recall.reason}` : ''}`;

  const cmpdData = cmpdKeys.map(k => {
    const c = getCmpd(k);
    const titStr = (() => {
      const t = c.titration;
      if (!t) return '';
      if (!t.preset) return t.customDose ? ` · Custom ${t.customDose}mg` : '';
      const preset = TITRATION_PRESETS[t.preset];
      const step = preset?.steps[t.currentStep ?? 0];
      return step ? ` · Step ${(t.currentStep ?? 0) + 1} · ${step.dose}mg` : '';
    })();
    return {
      label: c.label,
      freq: (c.freq || []).join(', ') || '—',
      periodCount: recentShots.filter(s => s.compound === k).length,
      total: stored.counters?.[k] || 0,
      titStr,
      blendStr: isBlend(c.blend) ? c.blend.map(i => `${i.name} ${i.mg} mg`).join(' · ') : '',
    };
  });

  const moodData = recentCIs.filter(c => c.mood);
  const avgMood = moodData.length
    ? (moodData.reduce((s, c) => s + c.mood, 0) / moodData.length).toFixed(1)
    : null;

  const weightData = recentCIs
    .filter(c => c.weight != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const wDelta = weightData.length >= 2
    ? (convertWeight(weightData[weightData.length - 1].weight, weightData[weightData.length - 1].weightUnit || wu, wu) - convertWeight(weightData[0].weight, weightData[0].weightUnit || wu, wu)).toFixed(1)
    : null;

  const bgData = recentCIs
    .filter(c => c.bloodGlucose != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const bpData = recentCIs
    .filter(c => (c.bloodPressureSystolic != null && c.bloodPressureDiastolic != null) || c.heartRate != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const symCounts = {};
  recentCIs.flatMap(c => c.symptoms || []).forEach(s => { symCounts[s] = (symCounts[s] || 0) + 1; });
  const topSyms = Object.entries(symCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const avgPerWeek = (recentShots.length / (rangeDays / 7)).toFixed(1);

  // ── sections ──────────────────────────────────────────────────────────────

  const protocolRows = cmpdData.map(d =>
    `<div class="row"><span class="lbl">${esc(d.label)}${esc(d.titStr)}${d.blendStr ? `<br><span class="val">${esc(d.blendStr)}</span>` : ''}</span><span class="val">${esc(d.freq)} &nbsp;·&nbsp; ${d.periodCount} dose${d.periodCount === 1 ? '' : 's'} this period &nbsp;·&nbsp; ${d.total} total</span></div>`
  ).join('');

  // Adherence: judged over rangeDays ending YESTERDAY, so a dose that isn't due yet today can't count against you.
  const adhStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - rangeDays);
  const adhEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const adherenceRows = cmpdKeys.map(k => {
    const c = getCmpd(k);
    const r = calcAdherence({ compound: c, compoundKey: k, shots: allShots, start: adhStart, end: adhEnd });
    if (r.scheduled === 0) return '';
    return `<div class="row"><span class="lbl">${esc(c.label)}</span><span class="val">${r.pct}% &nbsp;·&nbsp; ${r.logged} of ${r.scheduled} scheduled day${r.scheduled === 1 ? '' : 's'}</span></div>`;
  }).join('');
  const adherenceSection = adherenceRows ? `\n<h2>Adherence &middot; ${label}</h2>\n${adherenceRows}` : '';

  const recentTable = recentShots.length > 0 ? `
<h2>Recent Doses</h2>
<table><thead><tr><th>Date</th><th>Stack Item</th><th>Site</th><th>Dose</th></tr></thead><tbody>
${recentShots.slice(0, 14).map(s => {
    const c = getCmpd(s.compound);
    const isPen = isCountedDelivery(c.deliveryType);
    const site = (s.site || '').replace(/_/g, ' ').replace(':', ' ');
    // Same vial fallback as History (shot.vial → active/open → first with conc), so a shot with no
    // recorded vial still shows its mg and agrees with its ingredient breakdown.
    const vialForShot = resolveShotVial(stored.vials?.[s.compound], s.vial);
    const shotConc = splitShotConc(s, stored.vials?.[s.compound], c.deliveryType) ?? (vialForShot?.conc ? parseFloat(vialForShot.conc) : null);
    const pillCountDisplay = s.pillCount ?? s.vol;
    const doseDisplay = isPen
      ? (isCountOnlyPillShot(s) ? `${pillCountDisplay} pill${pillCountDisplay === 1 ? '' : 's'}` : `${s.vol}mg`)
      : shotConc ? `${(parseFloat(s.vol) * shotConc).toFixed(1)}mg` : `${s.vol} mL`;
    const blendText = formatBreakdown(shotBreakdown(s, stored.vials?.[s.compound], c.deliveryType));
    const recallInfo = shotRecallInfo(s, stored, recallMatches);
    return `<tr><td>${fmtS(s.date)}</td><td>${esc(c.label)}</td><td style="text-transform:capitalize">${esc(site)}</td><td>${esc(doseDisplay)}${blendText ? `<br><span style="font-size:10px;color:#666">${esc(blendText)}</span>` : ''}${recallInfo ? `<br><span style="font-size:10px;color:#c03030">${esc(recallText(recallInfo))}</span>` : ''}</td></tr>`;
  }).join('')}
</tbody></table>` : '';

  // Per-ingredient totals across every blend in range — same numbers as Trends' Blend Ingredients.
  const blendRows = ingredientTotals({ shots: recentShots, vials: stored.vials, getCmpd });
  const blendSection = blendRows.length ? `
<h2>Blend Ingredients &middot; ${label}</h2>
${blendRows.map(r => `<div class="row"><span class="lbl">${esc(r.name)}</span><span class="val">${num2(r.mg)}mg total &nbsp;·&nbsp; ${num2(r.mg / (rangeDays / 7))}mg / week</span></div>`).join('')}` : '';

  // One row per recalled vial that any in-range dose (not just the 14 shown above) came from.
  const recalledVials = new Map();
  recentShots.forEach(s => {
    const info = shotRecallInfo(s, stored, recallMatches);
    if (!info) return;
    const row = recalledVials.get(info.vial.id) || { info, compound: s.compound, doses: 0 };
    row.doses++;
    recalledVials.set(info.vial.id, row);
  });
  const recallSection = recalledVials.size ? `
<h2>Recalled Lots</h2>
${[...recalledVials.values()].map(({ info, compound, doses }) => `<div class="row"><span class="lbl">${esc(getCmpd(compound)?.label || '')} &middot; ${esc(info.vial.pharmacy || '')} &middot; lot ${esc(info.vial.lot || 'not recorded')}<br><span class="val">${esc(recallText(info))}${info.source === 'fda' ? `<br>${esc(info.recall.url)}` : ''}</span></span><span class="val">${doses} dose${doses === 1 ? '' : 's'} this period</span></div>`).join('')}` : '';

  const weightRow = weightData.length >= 2
    ? `<div class="row"><span class="lbl">Weight over period</span><span class="val">${convertWeight(weightData[0].weight, weightData[0].weightUnit || wu, wu)} ${wu} &rarr; ${convertWeight(weightData[weightData.length - 1].weight, weightData[weightData.length - 1].weightUnit || wu, wu)} ${wu}</span></div>`
    : '';

  const bgRow = bgData.length >= 2
    ? `<div class="row"><span class="lbl">Blood glucose over period</span><span class="val">${convertBloodGlucose(bgData[0].bloodGlucose, bgData[0].bloodGlucoseUnit || bgu, bgu)} ${bgu} &rarr; ${convertBloodGlucose(bgData[bgData.length - 1].bloodGlucose, bgData[bgData.length - 1].bloodGlucoseUnit || bgu, bgu)} ${bgu}</span></div>`
    : '';

  const bpRow = (() => {
    if (!bpData.length) return '';
    const latest = bpData[bpData.length - 1];
    const bpStr = (latest.bloodPressureSystolic != null && latest.bloodPressureDiastolic != null)
      ? `${latest.bloodPressureSystolic}/${latest.bloodPressureDiastolic} mmHg`
      : '';
    const hrStr = latest.heartRate != null ? `${latest.heartRate} bpm` : '';
    const combined = [bpStr, hrStr].filter(Boolean).join(' &nbsp;&middot;&nbsp; ');
    return combined ? `<div class="row"><span class="lbl">Blood pressure / heart rate (most recent)</span><span class="val">${combined}</span></div>` : '';
  })();

  // Trend charts — each needs 2+ readings in range.
  const t = c => new Date(c.date).getTime();
  const wPts = weightData.map(c => ({ t: t(c), v: convertWeight(c.weight, c.weightUnit || wu, wu) }));
  const gPts = bgData.map(c => ({ t: t(c), v: convertBloodGlucose(c.bloodGlucose, c.bloodGlucoseUnit || bgu, bgu) }));
  const bpBoth = bpData.filter(c => c.bloodPressureSystolic != null && c.bloodPressureDiastolic != null);
  const sysPts = bpBoth.map(c => ({ t: t(c), v: Number(c.bloodPressureSystolic) }));
  const diaPts = bpBoth.map(c => ({ t: t(c), v: Number(c.bloodPressureDiastolic) }));
  const rangeCap = (pts, unit) => `${num1(pts[0].v)} &rarr; ${num1(pts[pts.length - 1].v)} ${unit} &nbsp;&middot;&nbsp; min ${num1(Math.min(...pts.map(p => p.v)))} &middot; max ${num1(Math.max(...pts.map(p => p.v)))}`;
  const charts = [
    lineChart({ key: 'weight', title: `WEIGHT (${wu})`, series: [{ pts: wPts, color: '#006090' }], caption: wPts.length >= 2 ? rangeCap(wPts, wu) : '' }),
    lineChart({ key: 'bp', title: 'BLOOD PRESSURE (mmHg) &middot; systolic / diastolic', series: [{ pts: sysPts, color: '#c05820' }, { pts: diaPts, color: '#4a6aa0' }], caption: sysPts.length >= 2 ? `${sysPts[0].v}/${diaPts[0].v} &rarr; ${sysPts[sysPts.length - 1].v}/${diaPts[diaPts.length - 1].v}` : '' }),
    lineChart({ key: 'glucose', title: `BLOOD GLUCOSE (${bgu})`, series: [{ pts: gPts, color: '#2a8050' }], caption: gPts.length >= 2 ? rangeCap(gPts, bgu) : '' }),
  ].join('');

  const symsHtml = topSyms.length > 0
    ? `<div style="margin-top:14px"><div class="sec-lbl">NOTED SYMPTOMS</div>${topSyms.map(([s, n]) => `<span class="chip">${esc(s)} (${n}&times;)</span>`).join('')}</div>`
    : '';

  const wDeltaColor = wDelta !== null && parseFloat(wDelta) > 0 ? '#c05820' : '#2a8050';
  const wDeltaDisplay = wDelta !== null
    ? `<span style="color:${wDeltaColor}">${parseFloat(wDelta) > 0 ? '+' : ''}${wDelta}</span>`
    : '&mdash;';

  const checkInSection = recentCIs.length > 0 ? `
<h2>Wellness Check-ins &middot; ${label}</h2>
<div class="grid3">
  <div class="stat"><div class="snum">${recentCIs.length}</div><div class="slbl">Check-ins</div></div>
  <div class="stat"><div class="snum">${avgMood ? `${avgMood}<span style="font-size:14px">/5</span>` : '&mdash;'}</div><div class="slbl">Avg mood</div></div>
  <div class="stat"><div class="snum">${wDeltaDisplay}</div><div class="slbl">Weight &Delta; (${wu})</div></div>
</div>
${weightRow}
${bgRow}
${bpRow}
${charts}
${symsHtml}` : '';

  // Symptom timing is left out entirely (no "not enough data" text in a document for a clinician)
  // unless there is enough data AND a clear pattern.
  const timing = buildSymptomTiming({ checkIns, shots: allShots, getCmpd, sinceMs: rangeStart.getTime() });
  const timingSection = timing.eligibleCount >= TIMING_MIN_ELIGIBLE && timing.insights.length > 0 ? `
<h2>Symptom Timing &middot; ${label}</h2>
${timing.insights.map(i => `<div class="ins"><strong>${esc(i.symptom)}</strong><div class="note">${esc(describeInsight(i))}</div></div>`).join('')}
<div class="note-sm">Patterns in your own log, not medical advice &mdash; correlation isn't causation.</div>` : '';

  // ── HTML ──────────────────────────────────────────────────────────────────

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>MyRONKO Summary</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Georgia,'Times New Roman',serif;max-width:680px;margin:0 auto;padding:48px 32px 64px;color:#1a2432;line-height:1.65;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.bar{margin-bottom:18px}
.bar button{font-family:monospace;font-size:12px;letter-spacing:.06em;padding:10px 18px;border-radius:10px;border:1px solid #006090;background:#e8f4fb;color:#003850;cursor:pointer}
.logo{font-size:10px;letter-spacing:.28em;color:#006090;text-transform:uppercase;font-family:monospace;margin-bottom:6px}
h1{font-size:26px;font-weight:700;color:#0a1828;margin-bottom:5px}
.sub{font-size:11px;color:#607080;font-family:monospace;letter-spacing:.04em;margin-bottom:40px}
h2{font-size:10px;font-family:monospace;letter-spacing:.16em;text-transform:uppercase;color:#006090;border-bottom:1px solid #d0e4f0;padding-bottom:6px;margin:34px 0 16px}
.row{display:flex;justify-content:space-between;align-items:baseline;padding:7px 0;border-bottom:1px solid #f0f4f8;font-size:13px}
.row:last-child{border-bottom:none}
.lbl{color:#4a6070}
.val{font-weight:600;color:#0a1828;font-family:monospace;font-size:12px;text-align:right;max-width:62%}
.grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:12px}
.stat{background:#f4f8fc;border-radius:10px;padding:14px 16px}
.snum{font-size:28px;font-weight:700;color:#0a1828;font-family:monospace;line-height:1;margin-bottom:4px}
.slbl{font-size:8.5px;font-family:monospace;letter-spacing:.1em;color:#607080;text-transform:uppercase}
.chip{display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-family:monospace;background:#eef4f8;color:#4a6070;margin:2px}
.sec-lbl{font-size:8.5px;font-family:monospace;letter-spacing:.1em;text-transform:uppercase;color:#607080;margin-bottom:7px}
.chart{margin-top:16px;break-inside:avoid}
.cap{font-size:10px;font-family:monospace;color:#4a6070;margin-top:4px}
.ins{padding:8px 0;border-bottom:1px solid #f0f4f8;font-size:13px;break-inside:avoid}
.ins .note{color:#4a6070;font-size:12px}
.note-sm{font-size:10px;font-family:monospace;color:#90a0b0;margin-top:10px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:4px}
th{text-align:left;font-size:8px;font-family:monospace;letter-spacing:.12em;text-transform:uppercase;color:#607080;padding:0 8px 8px 0}
td{padding:6px 8px 6px 0;border-bottom:1px solid #f0f4f8;color:#1a2432}
tr:last-child td{border-bottom:none}
footer{margin-top:48px;padding-top:18px;border-top:1px solid #d0e4f0;font-size:10px;color:#90a0b0;font-family:monospace;line-height:1.9}
@media print{.noprint{display:none}body{padding:24px}}
</style>
</head>
<body>

<div class="bar noprint"><button type="button" onclick="window.print()">Save as PDF / Print</button></div>
<div class="logo">MyRONKO</div>
<h1>Health Summary</h1>
<div class="sub">Generated ${fmt(now)} &nbsp;&middot;&nbsp; ${rangeStart.getFullYear() !== now.getFullYear() ? fmt(rangeStart) : fmtS(rangeStart)} &ndash; ${fmtS(now)}</div>

<h2>Your Protocol</h2>
${protocolRows}

<h2>Dose Summary &middot; ${label}</h2>
<div class="grid3">
  <div class="stat"><div class="snum">${recentShots.length}</div><div class="slbl">Doses logged</div></div>
  <div class="stat"><div class="snum">${avgPerWeek}</div><div class="slbl">Avg per week</div></div>
  <div class="stat"><div class="snum">${streakStat}</div><div class="slbl">Day streak</div></div>
</div>
${adherenceSection}

${recentTable}
${recallSection}
${blendSection}
${checkInSection}
${timingSection}

<footer>
  This summary reflects data you logged in MyRONKO and is for personal reference only.<br>
  Always consult your healthcare provider before making any changes to your protocol.<br><br>
  myronko.com &nbsp;&middot;&nbsp; Exported ${fmt(now)}
</footer>

</body>
</html>`;
}

// Opens the report in a new tab (its "Save as PDF / Print" button does the rest). If the browser
// blocks the popup, falls back to downloading the .html so the button never silently does nothing.
// Returns 'opened' | 'downloaded' so the caller can tell the user which happened.
export function exportHealthSummary(args) {
  const now = args.now || new Date();
  const html = buildHealthSummaryHtml({ ...args, now });
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  if (window.open(url, '_blank')) {
    setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
    return 'opened';
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = `myronko-summary-${now.toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return 'downloaded';
}
